import { Autocomplete, Stack, TextField } from "@mui/material";
import { MultipleFacilityPicker } from "components/MultipleFacilityPicker";
import { taskStatusToLabel } from "components/Tasks/util.labels";
import { UnifiedTagsInput } from "components/UnifiedTagsInput";
import { TaskStatus } from "generated-graphql/graphql";
import { TAGS_QUERY } from "queries/tags";

import { CalendarFilter } from "./calendarFilter";
import { UserMultiSelect } from "./UserMultiSelect";

type CalendarFilterBuilderProps = {
  tenantId: string;
  value: CalendarFilter;
  onChange: (filter: CalendarFilter) => void;
};

const TASK_STATUSES: TaskStatus[] = [
  TaskStatus.Open,
  TaskStatus.InProgress,
  TaskStatus.Completed,
  TaskStatus.Canceled,
];

/** Filters for a custom calendar. Each is a dropdown that shows its picks as chips. */
export function CalendarFilterBuilder({ tenantId, value, onChange }: CalendarFilterBuilderProps) {
  const set = <K extends keyof CalendarFilter>(key: K, next: CalendarFilter[K]) =>
    onChange({ ...value, [key]: next });

  return (
    <Stack spacing={2}>
      <TextField
        size="small"
        label="Search"
        placeholder="e.g. permit, title:inspection, assignee:dave"
        helperText="Works like the task list search: free text and key:value filters."
        value={value.search}
        onChange={(event) => set("search", event.target.value)}
      />
      <Autocomplete
        multiple
        size="small"
        options={TASK_STATUSES}
        value={value.statuses}
        getOptionLabel={(status: TaskStatus) => taskStatusToLabel(status)}
        onChange={(_, statuses) => set("statuses", statuses)}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Status"
            placeholder={value.statuses.length === 0 ? "Any status" : ""}
          />
        )}
      />
      <MultipleFacilityPicker
        label="Facility"
        defaultSearchTerm={`tenantId:${tenantId}`}
        value={value.facilityIds}
        onChange={(facilities) =>
          set(
            "facilityIds",
            (facilities ?? []).map((facility) => facility.id)
          )
        }
        hideErrors
        textFieldProps={{ size: "small" }}
      />
      <UserMultiSelect
        label="Assignee"
        value={value.assigneeIds}
        onChange={(assigneeIds) => set("assigneeIds", assigneeIds)}
      />
      <UserMultiSelect
        label="Watcher"
        value={value.watcherIds}
        onChange={(watcherIds) => set("watcherIds", watcherIds)}
      />
      <UnifiedTagsInput
        label="Tags"
        size="small"
        value={value.tags}
        onChange={(tags) => set("tags", tags)}
        tagsQuery={TAGS_QUERY}
        queryConfig={{ tenantId, additionalSearch: "type:Task" }}
      />
      <UnifiedTagsInput
        label="Facility tags"
        size="small"
        value={value.facilityTags}
        onChange={(facilityTags) => set("facilityTags", facilityTags)}
        tagsQuery={TAGS_QUERY}
        queryConfig={{ tenantId, additionalSearch: "type:Facility" }}
      />
    </Stack>
  );
}
